import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Wand2, ShieldCheck, AlertTriangle, CheckCircle2,
  Clock, XCircle, FileText, Download, Copy, QrCode, MessageSquare,
  Phone, RotateCcw, ExternalLink, ChevronDown, ChevronUp, Building2,
  PackageCheck, Truck, UserCheck, Sparkles, Edit3,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import SignatureLinkQRModal from './SignatureLinkQRModal';
import CorrectionRequestModal from './CorrectionRequestModal';
import BaobabOperatorReviewFlow from './flows/BaobabOperatorReviewFlow';
import LoadingNoteWizardModal from './LoadingNoteWizardModal';
import BulkGenerateNotesDrawer from './BulkGenerateNotesDrawer';

// SEC-003 — sentinelle pour passer les guards `if (!token)`; le cookie HttpOnly est envoyé via axios withCredentials.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_LABELS = {
  shipper: 'Représentant chargement',
  consignee: 'Représentant déchargement',
  driver: 'Chauffeur',
  baobab_operator: 'Opérateur Baobab',
  observer: 'Observateur',
};

const PARTY_ICONS = {
  shipper: Building2,
  consignee: PackageCheck,
  driver: Truck,
  baobab_operator: ShieldCheck,
  observer: UserCheck,
};

const LINK_STATE_BADGE = {
  signed: { label: 'Signé', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  opened: { label: 'Ouvert', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  sent: { label: 'Envoyé', cls: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  created: { label: 'Créé', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  expired: { label: 'Expiré', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  revoked: { label: 'Révoqué', cls: 'bg-slate-100 text-slate-500 border-slate-300 line-through' },
  none: { label: 'Aucun lien', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

/**
 * PM Spec V3 13/05/2026 — LoadingNotesV3Panel
 * Panneau admin unifié pour la gestion des bons de chargement/déchargement.
 *
 * Affiche le dashboard live des signatures (3 acteurs × 2 bons = 6 liens).
 * Permet :
 *   - Auto-génération en 1 clic des 6 liens (POST /_auto-generate-links)
 *   - Régénération individuelle (POST /signature-links/{id}/regenerate)
 *   - Validation 1-clic opérateur Baobab (modal BaobabOperatorReviewFlow)
 *   - Demande de correction ciblée (CorrectionRequestModal)
 *   - Mode Expert legacy (LoadingNoteWizardModal) en fallback
 */
const LoadingNotesV3Panel = ({ operation, currentUser, entityKind = 'operation' }) => {
  const [statusV3, setStatusV3] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [qrLink, setQrLink] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, noteType: null });
  const [correctionModal, setCorrectionModal] = useState({ open: false, noteId: null, noteType: null });
  const [expertModal, setExpertModal] = useState({ open: false, type: null });
  const [expandedNote, setExpandedNote] = useState({ loading: true, unloading: true });
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [voyageGenerating, setVoyageGenerating] = useState({}); // {voyageKey-type: true}

  const opId = operation?.id;
  const baseUrl = entityKind === 'transfer'
    ? `${API}/transfers/${opId}/loading-notes`
    : `${API}/operations/${opId}/loading-notes`;

  const authHeaders = useMemo(() => {
    // SEC-003 — sentinelle, l'auth réelle passe par le cookie HttpOnly (withCredentials)
    return { Authorization: 'Bearer cookie-auth' };
  }, []);

  // PM Spec V3 — Axios instance (auth via cookie HttpOnly grâce au monkey-patch
  // axios.create dans AuthContext qui force withCredentials + interceptors).
  const ax = useMemo(() => axios.create(), []);

  const fetchStatus = useCallback(async () => {
    if (!opId) return;
    setLoading(true);
    try {
      const r = await ax.get(`${baseUrl}/_signatures-status`);
      setStatusV3(r.data || null);
    } catch (err) {
      console.error('V3 status fetch error', err);
      toast({
        title: 'Erreur de chargement',
        description: err?.response?.data?.detail || 'Impossible de charger le statut des signatures.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [opId, baseUrl, ax]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Multi-Bons V1 — génère 1 bon manquant (BC ou BD) pour un voyage spécifique
  const handleGenerateMissing = useCallback(async (voyage, missingType) => {
    if (voyage.assignmentIndex === null || voyage.assignmentIndex === undefined
        || !voyage.rotationIndex) {
      toast({ title: 'Voyage legacy', description: "Utilisez le drawer 'Générer en masse'", variant: 'destructive' });
      return;
    }
    const cellKey = `${voyage.key}-${missingType}`;
    setVoyageGenerating(prev => ({ ...prev, [cellKey]: true }));
    try {
      await ax.post(`${baseUrl}/generate-bulk`, {
        rotations: [{
          assignmentIndex: voyage.assignmentIndex,
          rotationsPlanned: voyage.rotationIndex,
        }],
        generateLoading: missingType === 'loading',
        generateUnloading: missingType === 'unloading',
        pairBcBd: false,
      });
      toast({ title: 'Bon créé', description: `Bon de ${missingType === 'loading' ? 'chargement' : 'déchargement'} généré` });
      fetchStatus();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Génération impossible', variant: 'destructive' });
    } finally {
      setVoyageGenerating(prev => {
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    }
  }, [ax, baseUrl, fetchStatus]);

  // Génère tous les bons manquants visibles dans la vue voyage en 1 clic
  const handleGenerateAllMissing = useCallback(async () => {
    if (!statusV3?.voyages) return;
    const rotationsMap = {};
    let needsBC = false, needsBD = false;
    for (const v of statusV3.voyages) {
      if (v.assignmentIndex === null || v.assignmentIndex === undefined || !v.rotationIndex) continue;
      const ai = v.assignmentIndex;
      rotationsMap[ai] = Math.max(rotationsMap[ai] || 0, v.rotationIndex);
      if (!v.loadingNote) needsBC = true;
      if (!v.unloadingNote) needsBD = true;
    }
    if (!needsBC && !needsBD) {
      toast({ title: 'Aucun bon à générer', description: 'Tous les voyages ont déjà leurs bons' });
      return;
    }
    setVoyageGenerating(prev => ({ ...prev, _all: true }));
    try {
      await ax.post(`${baseUrl}/generate-bulk`, {
        rotations: Object.entries(rotationsMap).map(([ai, rot]) => ({
          assignmentIndex: Number(ai),
          rotationsPlanned: rot,
        })),
        generateLoading: needsBC,
        generateUnloading: needsBD,
        pairBcBd: false,
      });
      toast({ title: 'Bons générés', description: 'Les bons manquants ont été créés' });
      fetchStatus();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Génération impossible', variant: 'destructive' });
    } finally {
      setVoyageGenerating(prev => {
        const next = { ...prev };
        delete next._all;
        return next;
      });
    }
  }, [statusV3, ax, baseUrl, fetchStatus]);

  const handleAutoGenerate = async (forceRegenerate = false) => {
    setGenerating(true);
    try {
      const r = await ax.post(`${baseUrl}/_auto-generate-links`, {
        expiresInHours: 48,
        includeBaobabOperator: true,
        forceRegenerate,
      });
      // Backend retourne `linksCreated`/`linksSkipped` (pas `created`/`skipped`)
      const created = r.data?.linksCreated ?? r.data?.created ?? 0;
      const skipped = r.data?.linksSkipped ?? r.data?.skipped ?? 0;
      const totalParties = (r.data?.results || []).length;
      const noContact = (r.data?.results || []).filter(x => !x.skipped && !x.url).length;
      let desc = `${created} lien(s) créé(s), ${skipped} déjà actif(s).`;
      if (totalParties === 0 && created === 0 && skipped === 0) {
        desc = "Aucun lien à générer. Vérifiez les contacts (chargeur/déchargeur/chauffeur) sur l'opération.";
      }
      toast({
        title: created > 0 ? 'Liens générés' : 'Génération terminée',
        description: desc,
        variant: created === 0 && skipped === 0 ? 'destructive' : 'default',
      });
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Échec de la génération automatique.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateLink = async (noteId, linkId) => {
    try {
      await ax.post(`${baseUrl}/${noteId}/signature-links/${linkId}/regenerate`);
      toast({ title: 'Lien régénéré' });
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Régénération échouée.',
        variant: 'destructive',
      });
    }
  };

  // PM Spec V3 — Téléchargement PDF avec auth (axios + blob, sinon window.open échoue en 401)
  const handleDownloadPdf = async (noteId, noteNumber) => {
    try {
      const r = await ax.get(`${baseUrl}/${noteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${noteNumber || noteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.status === 401
          ? 'Session expirée. Reconnectez-vous.'
          : (err?.response?.data?.detail || 'Téléchargement impossible.'),
        variant: 'destructive',
      });
    }
  };

  // PM Spec V3 — Génération individuelle pour 1 acteur précis
  const handleGenerateOne = async (noteId, party) => {
    try {
      await ax.post(`${baseUrl}/${noteId}/signature-links`, {
        party,
        targetName: null,
        targetPhone: null,
        uiLevel: party === 'driver' || party === 'baobab_operator' ? 'standard' : 'lite',
        expiresInHours: 48,
      });
      toast({ title: 'Lien créé', description: `Pour ${PARTY_LABELS[party] || party}` });
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Création du lien impossible.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyLink = async (noteId, linkId) => {
    try {
      const noteResp = await ax.get(`${baseUrl}/${noteId}/signature-links`);
      const link = (noteResp.data?.links || []).find(l => l.id === linkId);
      if (!link?.url) {
        toast({ title: 'Lien indisponible', variant: 'destructive' });
        return;
      }
      await navigator.clipboard.writeText(link.url);
      await ax.post(`${baseUrl}/${noteId}/signature-links/${linkId}/mark-sent`, { channel: 'copy' }).catch(() => null);
      toast({ title: 'Lien copié', description: 'Collez-le dans WhatsApp/SMS.' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Copie impossible.', variant: 'destructive' });
    }
  };

  const handleWhatsAppLink = async (noteId, linkId, party) => {
    try {
      const noteResp = await ax.get(`${baseUrl}/${noteId}/signature-links`);
      const link = (noteResp.data?.links || []).find(l => l.id === linkId);
      if (!link?.url) {
        toast({ title: 'Lien indisponible', variant: 'destructive' });
        return;
      }
      const phone = (link.targetPhone || '').replace(/[^\d+]/g, '');
      const greet = link.targetName ? `Bonjour ${link.targetName},` : 'Bonjour,';
      const ref = operation?.reference || operation?.requestNumber || '';
      const text = encodeURIComponent(
        `${greet}\nMerci de signer (${PARTY_LABELS[party] || party}) le bon ${ref} :\n${link.url}\n\nCode : ${link.code || ''}`
      );
      const url = phone ? `https://wa.me/${phone.replace('+', '')}?text=${text}` : `https://wa.me/?text=${text}`;
      window.open(url, '_blank', 'noopener');
      await ax.post(`${baseUrl}/${noteId}/signature-links/${linkId}/mark-sent`, { channel: 'whatsapp_manual' }).catch(() => null);
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleShowQR = async (noteId, linkId) => {
    try {
      const noteResp = await ax.get(`${baseUrl}/${noteId}/signature-links`);
      const link = (noteResp.data?.links || []).find(l => l.id === linkId);
      const note = await ax.get(`${baseUrl}/${noteId}`).then(r => r.data).catch(() => null);
      if (link && note) setQrLink({ link, note });
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleCorrectionSubmit = async (payload) => {
    if (!correctionModal.noteId) return;
    try {
      await ax.post(`${baseUrl}/${correctionModal.noteId}/correction-request`, payload);
      toast({ title: 'Correction demandée', description: 'Les acteurs ciblés vont recevoir un nouveau lien.' });
      setCorrectionModal({ open: false, noteId: null, noteType: null });
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Échec.',
        variant: 'destructive',
      });
    }
  };

  if (!opId) return null;

  if (loading && !statusV3) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-500" data-testid="v3-panel-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement du tableau de bord signatures…
      </div>
    );
  }

  const renderNoteCard = (noteKey, label) => {
    const noteStatus = statusV3?.[noteKey];
    if (!noteStatus) {
      // Pas encore de bon créé
      return (
        <Card key={noteKey} className="border-slate-200" data-testid={`v3-note-card-${noteKey === 'loadingNote' ? 'loading' : 'unloading'}`}>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-base font-bold text-slate-900">{label}</div>
                <div className="text-xs text-slate-500">Aucun bon créé pour cette opération</div>
              </div>
              <Badge variant="outline" className="text-slate-500">Non créé</Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid={`v3-open-expert-${noteKey === 'loadingNote' ? 'loading' : 'unloading'}`}
              onClick={() => setExpertModal({ open: true, type: noteKey === 'loadingNote' ? 'loading' : 'unloading' })}
              className="gap-2 text-slate-500 hover:text-slate-700"
            >
              <Edit3 className="w-3.5 h-3.5" /> Avancé : saisie manuelle
            </Button>
          </CardContent>
        </Card>
      );
    }

    const noteType = noteStatus.type;
    const isExpanded = expandedNote[noteType];
    const fullySigned = noteStatus.fullySigned;
    const rejected = !!noteStatus.rejectedAt;
    const preValidated = !!noteStatus.preValidatedByOperatorAt;
    const operatorParty = (noteStatus.parties || []).find(p => p.party === 'baobab_operator');
    const operatorAlreadySigned = operatorParty?.signed;
    const fieldParties = (noteStatus.parties || []).filter(p => p.party !== 'baobab_operator');
    const fieldFullySigned = fieldParties.length > 0 && fieldParties.every(p => p.signed);

    return (
      <Card
        key={noteKey}
        className={`border-2 ${fullySigned ? 'border-emerald-300 bg-emerald-50/30' : rejected ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
        data-testid={`v3-note-card-${noteType}`}
      >
        <CardContent className="p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="flex items-center gap-2 text-left flex-1"
              onClick={() => setExpandedNote(s => ({ ...s, [noteType]: !s[noteType] }))}
              data-testid={`v3-toggle-${noteType}`}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              <FileText className={`w-5 h-5 ${fullySigned ? 'text-emerald-600' : 'text-slate-500'}`} />
              <div>
                <div className="text-sm font-bold text-slate-900">{label}</div>
                <div className="text-xs text-slate-500">{noteStatus.noteNumber}</div>
              </div>
            </button>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={
                fullySigned ? 'bg-emerald-600 text-white border-0' :
                rejected ? 'bg-red-600 text-white border-0' :
                preValidated ? 'bg-blue-600 text-white border-0' :
                'bg-amber-100 text-amber-800 border-amber-300'
              }>
                {fullySigned ? `✓ Signé ${noteStatus.signaturesCount}/3` :
                 rejected ? 'Rejeté' :
                 preValidated ? `Pré-validé ${noteStatus.signaturesCount}/3` :
                 `${noteStatus.signaturesCount}/3 signatures`}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadPdf(noteStatus.noteId, noteStatus.noteNumber)}
                title="Télécharger le PDF"
                data-testid={`v3-pdf-${noteType}`}
                className="h-8 w-8 p-0"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {!isExpanded && null}

          {isExpanded && (
            <>
              {/* Parties (3 cartes) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {(noteStatus.parties || []).map((p) => {
                  const Icon = PARTY_ICONS[p.party] || UserCheck;
                  const stateBadge = LINK_STATE_BADGE[p.linkState || 'none'] || LINK_STATE_BADGE.none;
                  const isOperator = p.party === 'baobab_operator';
                  const partyLabel = PARTY_LABELS[p.party] || p.party;
                  return (
                    <div
                      key={p.party}
                      className={`rounded-lg border p-3 space-y-2 ${
                        p.signed ? 'bg-emerald-50 border-emerald-300' :
                        isOperator ? 'bg-blue-50/50 border-blue-200' :
                        'bg-white border-slate-200'
                      }`}
                      data-testid={`v3-party-card-${noteType}-${p.party}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${p.signed ? 'text-emerald-600' : isOperator ? 'text-blue-600' : 'text-slate-500'}`} />
                        <div className="text-xs font-bold text-slate-700 uppercase truncate">{partyLabel}</div>
                      </div>
                      <div className="text-sm font-medium text-slate-900 leading-tight min-h-[1.2rem]">
                        {p.targetName || p.preFillName || (isOperator ? (currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email : '—') : '—')}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {p.signed ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px] gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Signé
                          </Badge>
                        ) : (
                          <Badge variant="outline" className={`${stateBadge.cls} text-[10px]`}>
                            {stateBadge.label}
                          </Badge>
                        )}
                      </div>
                      {/* Actions par lien — labels clairs */}
                      {!p.signed && p.linkId && !isOperator && (
                        <div className="grid grid-cols-2 gap-1 pt-2 border-t border-slate-200">
                          <Button
                            variant="outline" size="sm" className="h-7 px-2 text-[11px] gap-1 justify-start"
                            onClick={() => handleCopyLink(noteStatus.noteId, p.linkId)}
                            data-testid={`v3-copy-${noteType}-${p.party}`}
                          >
                            <Copy className="w-3 h-3" /> Copier
                          </Button>
                          <Button
                            variant="outline" size="sm" className="h-7 px-2 text-[11px] gap-1 justify-start text-emerald-700 border-emerald-300"
                            onClick={() => handleWhatsAppLink(noteStatus.noteId, p.linkId, p.party)}
                            data-testid={`v3-whatsapp-${noteType}-${p.party}`}
                          >
                            <MessageSquare className="w-3 h-3" /> WhatsApp
                          </Button>
                          <Button
                            variant="outline" size="sm" className="h-7 px-2 text-[11px] gap-1 justify-start"
                            onClick={() => handleShowQR(noteStatus.noteId, p.linkId)}
                            data-testid={`v3-qr-${noteType}-${p.party}`}
                          >
                            <QrCode className="w-3 h-3" /> QR code
                          </Button>
                          <Button
                            variant="outline" size="sm" className="h-7 px-2 text-[11px] gap-1 justify-start text-slate-600"
                            onClick={() => handleRegenerateLink(noteStatus.noteId, p.linkId)}
                            data-testid={`v3-regen-${noteType}-${p.party}`}
                          >
                            <RotateCcw className="w-3 h-3" /> Régénérer
                          </Button>
                        </div>
                      )}
                      {/* État "Aucun lien" → CTA pour générer juste celui-ci */}
                      {!p.signed && !p.linkId && !isOperator && (
                        <div className="pt-2 border-t border-slate-200 space-y-1">
                          <Button
                            variant="outline" size="sm"
                            className="w-full h-7 text-[11px] gap-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleGenerateOne(noteStatus.noteId, p.party)}
                            data-testid={`v3-generate-one-${noteType}-${p.party}`}
                          >
                            <Wand2 className="w-3 h-3" /> Générer le lien
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="w-full h-6 text-[10px] gap-1 text-slate-500 hover:text-slate-700"
                            onClick={() => setExpertModal({ open: true, type: noteType })}
                            data-testid={`v3-expert-actor-${noteType}-${p.party}`}
                          >
                            <Edit3 className="w-3 h-3" /> Saisir manuellement
                          </Button>
                        </div>
                      )}
                      {p.signedAt && (
                        <div className="text-[10px] text-slate-500">
                          {new Date(p.signedAt).toLocaleString('fr-FR')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Decision area : Validation opérateur + correction */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                {!operatorAlreadySigned && !rejected && (
                  <Button
                    size="sm"
                    onClick={() => setReviewModal({ open: true, noteType })}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                    data-testid={`v3-validate-operator-${noteType}`}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {fieldFullySigned ? 'Valider (signature + cachet)' : 'Pré-valider'}
                  </Button>
                )}
                {operatorAlreadySigned && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1">
                    <ShieldCheck className="w-3 h-3" /> Validé par l'opérateur
                  </Badge>
                )}
                {!rejected && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setCorrectionModal({ open: true, noteId: noteStatus.noteId, noteType })}
                    className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-2"
                    data-testid={`v3-correction-${noteType}`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" /> Demander correction
                  </Button>
                )}
                <Button
                  size="sm" variant="outline"
                  onClick={() => setExpertModal({ open: true, type: noteType })}
                  className="gap-2 ml-auto text-slate-600"
                  data-testid={`v3-expert-${noteType}`}
                >
                  <Edit3 className="w-3.5 h-3.5" /> Mode expert
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-3" data-testid="loading-notes-v3-panel">
      {/* Toolbar haut */}
      <div className="flex flex-wrap items-center gap-2 justify-between bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-600" />
          <div>
            <div className="text-sm font-bold text-slate-900">Bons & signatures (V3)</div>
            <div className="text-xs text-slate-500">Distribuez les liens aux 3 acteurs en 1 clic</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {entityKind === 'operation' && (operation?.numberOfTrucks > 1 || true) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkDrawerOpen(true)}
              className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="open-bulk-generate-notes"
            >
              <Truck className="w-3.5 h-3.5" />
              Générer en masse
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => handleAutoGenerate(false)}
            disabled={generating}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            data-testid="v3-auto-generate-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Envoyer le bon
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={fetchStatus}
            disabled={loading}
            className="gap-1 text-slate-600"
            data-testid="v3-refresh-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Suggestions automatiques (Multi-Bons V1 — PM Spec 14/02/2026) */}
      {statusV3?.suggestedNextActions && statusV3.suggestedNextActions.length > 0 && (
        <div className="space-y-2" data-testid="v3-suggested-actions">
          {statusV3.suggestedNextActions.map((sa, idx) => (
            sa.type === 'create_unloading' ? (
              <div key={idx}
                   className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                <div className="flex items-center gap-2 text-amber-900">
                  <PackageCheck className="w-4 h-4" />
                  <span>
                    Bon de chargement <b>{sa.loadingNoteNumber}</b> signé.
                    {sa.rotationIndex ? <> Voyage <b>n°{sa.rotationIndex}</b>.</> : null} Générer le bon de déchargement ?
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkDrawerOpen(true)}
                  className="border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid={`v3-suggest-create-bd-${idx}`}
                >
                  Générer le BD
                </Button>
              </div>
            ) : null
          ))}
        </div>
      )}

      {/* Vue groupée par voyage (Multi-Bons V1) — affichée seulement si >1 voyage */}
      {statusV3?.voyages && statusV3.voyages.length > 1 && (
        <div className="border border-emerald-200 rounded-lg bg-white overflow-hidden" data-testid="v3-voyages-panel">
          <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Vue par voyage ({statusV3.voyagesCount})
            </div>
            <div className="flex items-center gap-3">
              <div className="text-[11px] text-emerald-700 hidden sm:block">
                BC = chargement · BD = déchargement
              </div>
              {(() => {
                const missingCount = (statusV3.voyages || []).reduce((sum, v) => {
                  if (v.assignmentIndex === null || v.assignmentIndex === undefined || !v.rotationIndex) return sum;
                  return sum + (v.loadingNote ? 0 : 1) + (v.unloadingNote ? 0 : 1);
                }, 0);
                if (missingCount === 0) return null;
                return (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGenerateAllMissing}
                    disabled={voyageGenerating._all}
                    className="border-emerald-400 text-emerald-700 hover:bg-emerald-100 gap-2 h-7 text-xs"
                    data-testid="v3-generate-all-missing"
                  >
                    {voyageGenerating._all
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Wand2 className="w-3 h-3" />}
                    Générer {missingCount} bon{missingCount > 1 ? 's' : ''} manquant{missingCount > 1 ? 's' : ''}
                  </Button>
                );
              })()}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Voyage</th>
                  <th className="px-3 py-2 text-left">Camion</th>
                  <th className="px-3 py-2 text-left">BC</th>
                  <th className="px-3 py-2 text-left">BD</th>
                </tr>
              </thead>
              <tbody>
                {statusV3.voyages.map((v) => {
                  const isLegacy = v.assignmentIndex === null || v.assignmentIndex === undefined || !v.rotationIndex;
                  const bcKey = `${v.key}-loading`;
                  const bdKey = `${v.key}-unloading`;
                  return (
                  <tr key={v.key} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono">
                      {v.rotationIndex ? `R${v.rotationIndex}` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {!isLegacy ? `Camion #${v.assignmentIndex + 1}` : 'Legacy'}
                    </td>
                    <td className="px-3 py-2">
                      {v.loadingNote ? (
                        <span className={v.loadingNote.fullySigned ? 'text-emerald-700' : 'text-amber-700'}>
                          {v.loadingNote.fullySigned ? '✓' : '⏳'} {v.loadingNote.noteNumber}
                          <span className="text-slate-400 ml-1">({v.loadingNote.signaturesCount}/3)</span>
                        </span>
                      ) : isLegacy ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleGenerateMissing(v, 'loading')}
                          disabled={!!voyageGenerating[bcKey]}
                          className="text-emerald-600 hover:text-emerald-800 hover:underline italic disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`v3-generate-missing-${v.key}-loading`}
                        >
                          {voyageGenerating[bcKey] && <Loader2 className="w-3 h-3 animate-spin" />}
                          {voyageGenerating[bcKey] ? 'génération…' : '+ générer le BC'}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {v.unloadingNote ? (
                        <span className={v.unloadingNote.fullySigned ? 'text-emerald-700' : 'text-amber-700'}>
                          {v.unloadingNote.fullySigned ? '✓' : '⏳'} {v.unloadingNote.noteNumber}
                          <span className="text-slate-400 ml-1">({v.unloadingNote.signaturesCount}/3)</span>
                        </span>
                      ) : isLegacy ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleGenerateMissing(v, 'unloading')}
                          disabled={!!voyageGenerating[bdKey]}
                          className="text-emerald-600 hover:text-emerald-800 hover:underline italic disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`v3-generate-missing-${v.key}-unloading`}
                        >
                          {voyageGenerating[bdKey] && <Loader2 className="w-3 h-3 animate-spin" />}
                          {voyageGenerating[bdKey] ? 'génération…' : '+ générer le BD'}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes cards */}
      <div className="space-y-3">
        {renderNoteCard('loadingNote', 'Bon de chargement')}
        {renderNoteCard('unloadingNote', 'Bon de déchargement')}
      </div>

      {/* QR modal */}
      {qrLink && (
        <SignatureLinkQRModal
          link={qrLink.link}
          note={qrLink.note}
          onClose={() => setQrLink(null)}
          onShown={async (channel) => {
            try {
              await ax.post(`${baseUrl}/${qrLink.note.id}/signature-links/${qrLink.link.id}/mark-sent`, { channel });
            } catch { /* silent */ }
          }}
        />
      )}

      {/* Operator validation modal */}
      <Dialog
        open={reviewModal.open}
        onOpenChange={(o) => {
          if (!o) {
            setReviewModal({ open: false, noteType: null });
            fetchStatus();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Validation opérateur Baobab</DialogTitle>
          </DialogHeader>
          {reviewModal.open && (
            <BaobabOperatorReviewFlow
              type={reviewModal.noteType}
              operation={operation}
              operatorName={currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email : ''}
              endpointPrefix={baseUrl}
              authHeaders={authHeaders}
              linkId={null}
              onCompleted={() => {
                setReviewModal({ open: false, noteType: null });
                fetchStatus();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Correction request modal */}
      <CorrectionRequestModal
        open={correctionModal.open}
        onOpenChange={(o) => !o && setCorrectionModal({ open: false, noteId: null, noteType: null })}
        noteType={correctionModal.noteType}
        submitting={false}
        onSubmit={handleCorrectionSubmit}
      />

      {/* Expert mode legacy modal */}
      <LoadingNoteWizardModal
        open={expertModal.open}
        onOpenChange={(o) => {
          setExpertModal(s => ({ ...s, open: o }));
          if (!o) fetchStatus();
        }}
        operation={operation}
        type={expertModal.type}
        entityKind={entityKind}
        onSuccess={() => fetchStatus()}
      />

      {/* Bulk Generate Notes Drawer (Multi-Bons V1 14/02/2026) */}
      <BulkGenerateNotesDrawer
        open={bulkDrawerOpen}
        onClose={() => setBulkDrawerOpen(false)}
        operationId={opId}
        operationWeight={operation?.weight}
        onGenerated={() => fetchStatus()}
      />
    </div>
  );
};

export default LoadingNotesV3Panel;
